import i18n from '../i18n/config';

/**
 * Relative time ("2h ago" / "il y a 2 h"), locale-aware.
 *
 * §9 — this used to be TWO copies of an English-only formatter (useSeniors.ts
 * and GeofenceBreachAlert.tsx). Neither was translated, so a French caregiver
 * read "Hors ligne — vu il y a 2h ago" as the largest text on the status card.
 *
 * WHY THE STRINGS ARE HERE AND NOT IN THE LOCALE JSON
 * They are a closed set of four with a numeric argument each, consumed by a
 * plain function rather than a component. Threading `t` through every caller
 * (including non-React ones) to interpolate four fragments buys nothing over
 * keeping the shapes together where the branching lives. The FR forms are
 * CEO-approved, 2026-08-11.
 *
 * ⚠️ THE FR FORMS INCLUDE "il y a". Any sentence that embeds this must NOT add
 * its own — `dashboard.heroCard.offlineSince` was "Hors ligne — vu il y a
 * {{time}}" and became "Hors ligne — vu {{time}}" for exactly this reason.
 * Check the frame before adding a new consumer.
 */
export function formatTimeAgo(input: string | number, locale?: string): string {
  const then = typeof input === 'number' ? input : new Date(input).getTime();
  if (!Number.isFinite(then)) return '';

  const lang = (locale ?? i18n.language ?? 'en').toLowerCase();
  const fr = lang.startsWith('fr');

  // Clamp negatives: a device clock running ahead should read "just now",
  // never "il y a -3 min".
  const diffMs = Math.max(0, Date.now() - then);
  const min = Math.floor(diffMs / 60_000);

  if (min < 1) return fr ? "à l'instant" : 'just now';
  if (min < 60) return fr ? `il y a ${min} min` : `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return fr ? `il y a ${hr} h` : `${hr}h ago`;

  const days = Math.floor(hr / 24);
  return fr ? `il y a ${days} j` : `${days}d ago`;
}

export default formatTimeAgo;
